import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

// The hub has no "list bots" endpoint (bot identity is owned gateway-side), so
// the operator selects the (system, botId) pair directly. We persist it to
// localStorage so it survives reloads. A1: no default bot — pages act on nothing until one is chosen
// (it used to be chatbot/7598, so a fresh browser silently edited that bot).
interface BotScope {
  system: string;
  botId: string;
}

interface BotContextValue extends BotScope {
  setScope: (scope: BotScope) => void;
}

const STORAGE_KEY = 'hub-ui.botScope';
const DEFAULT_SCOPE: BotScope = { system: '', botId: '' };

function loadScope(): BotScope {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SCOPE;
    const parsed = JSON.parse(raw) as Partial<BotScope>;
    if (typeof parsed.system === 'string' && typeof parsed.botId === 'string') {
      return { system: parsed.system, botId: parsed.botId };
    }
  } catch {
    /* fall through to default */
  }
  return DEFAULT_SCOPE;
}

const BotContext = createContext<BotContextValue | null>(null);

export function BotProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<BotScope>(loadScope);

  const setScope = useCallback((next: BotScope) => {
    setScopeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, []);

  const value = useMemo<BotContextValue>(
    () => ({ ...scope, setScope }),
    [scope, setScope],
  );

  return <BotContext.Provider value={value}>{children}</BotContext.Provider>;
}

export function useBot(): BotContextValue {
  const ctx = useContext(BotContext);
  if (!ctx) throw new Error('useBot must be used within <BotProvider>');
  return ctx;
}
